
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { IssueListComponent } from './core/components/issue-list/issue-list.component';
import { IssueViewComponent } from './core/components/issue-view/issue-view.component';
import { IssuesSettingsPageComponent } from './core/components/issues-settings-page/issues-settings-page.component';
import { LabelListComponent } from './core/components/label-list/label-list.component';
import { LabelViewComponent } from './core/components/label-view/label-view.component';
import { TaskSettingsPageComponent } from './core/components/task-settings-page/task-settings-page.component';

import { BillingComponent } from './core/components/billing/billing.component';
import { CompanyViewComponent } from './core/components/company-view/company-view.component';
import { DeveloperPageComponent } from './core/components/developer-page/developer-page.component';
import { FormVariantComponent } from './core/components/form-variant/form-variant.component';
import { FormsListComponent } from './core/components/forms-list/forms-list.component';
import { GroupListComponent } from './core/components/group-list/group-list.component';
import { GroupViewComponent } from './core/components/group-view/group-view.component';
import { MobileDeviceListComponent } from './core/components/mobile-device-list/mobile-device-list.component';
import { MobileDeviceViewComponent } from './core/components/mobile-device-view/mobile-device-view.component';
import { NoAccessPageComponent } from './core/components/no-access-page/no-access-page.component';
import { AnnouncementFormComponent } from './core/components/announcement-form/announcement-form.component';
import { AnnouncementListComponent } from './core/components/announcement-list/announcement-list.component';
import { JobListComponent } from './core/components/job-list/job-list.component';
import { JobDetailComponent } from './core/components/job-detail/job-detail.component';
import { NotificationAdminDetailComponent } from './core/components/notification-admin-detail/notification-admin-detail.component';
import { NotificationDiagnosticsPageComponent } from './core/components/notification-diagnostics/notification-diagnostics-page.component';
import { TemplateVariantComponent } from './core/components/template-variant/template-variant.component';
import { TemplatesComponent } from './core/components/templates/templates.component';
import { TenantListComponent } from './core/components/tenant-list/tenant-list.component';
import { TenantViewComponent } from './core/components/tenant-view/tenant-view.component';
import { TimeEntryCategoryListComponent } from './core/components/time-entry-category-list/time-entry-category-list.component';
import { TimeEntryCategoryViewComponent } from './core/components/time-entry-category-view/time-entry-category-view.component';
import { UserListComponent } from './core/components/users-list/user-list.component';
import { UserProfileComponent } from './core/components/user-profile/user-profile.component';
import { UserViewComponent } from './core/components/user-view/user-view.component';
import { AuthGuard } from './guard/auth.guard';
import { PermissionGuard } from './guard/permission.guard';
import { OrderViewComponent } from './order-module/containers/order-view/order-view.component';
import { OrdersListComponent } from './order-module/containers/orders-list/orders-list.component';
import { SettingsComponent } from './rent-module/components/settings/settings.component';
import { TaskAssignmentComponent } from './rent-module/components/task-assignment-component/task-assignment.component';
import { ArticlesListComponent } from './rent-module/containers/articles-list/articles-list.component';
import { CustomerViewComponent } from './rent-module/containers/customer-view/customer-view.component';
import { CustomersListComponent } from './rent-module/containers/customers-list/customers-list.component';
import { DashboardComponent } from './rent-module/containers/dashboard/dashboard.component';
import { GeneralSettingsComponent } from './rent-module/containers/general-settings/general-settings.component';
import { LocationViewComponent } from './rent-module/containers/location-view/location-view.component';
import { LocationsListComponent } from './rent-module/containers/locations-list/locations-list.component';
import { MyTasksComponent } from './rent-module/containers/my-tasks/my-tasks.component';
import { ResourceViewComponent } from './rent-module/containers/resource-view/resource-view.component';
import { ResourcesListComponent } from './rent-module/containers/resources-list/resources-list.component';
import { SettingsOverviewComponent } from './rent-module/containers/settings-overview/settings-overview.component';
import { TasksComponent } from './rent-module/containers/tasks/tasks.component';
import { LayoutComponent } from './shared-module/components/layout/layout.component';
import { NotificationsPageComponent } from './shared-module/containers/notifications-page/notifications-page.component';
import { NonLicensePageComponent } from './shared-module/components/non-license-page/non-license-page.component';
import { IssuesPageComponent } from './rent-module/containers/issues-page/issues-page.component';
import { TasksPageComponent } from './rent-module/containers/tasks-page/tasks-page.component';
import { ToursDetailPageComponent } from './tour-planning/containers/tours-detail-page/tours-detail-page.component';
import { ToursPlannerPageComponent } from './tour-planning/containers/tours-planner-page/tours-planner-page.component';
import { TaskViewComponent } from './rent-module/containers/task-view/task-view.component';

const routes: Routes = [
  {
    path: '',
    component: LayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_dashboard_view' }
      },
      {
        path: 'tour-planner',
        component: ToursPlannerPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_tour_view'}
      },
      {
        path: 'tour-planner/detail/:id',
        component: ToursDetailPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_tour_view'}
      },
      {
        path: 'orders',
        component: OrdersListComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_order_view' },
      },
      {
        path: 'orders/order/:id',
        component: OrderViewComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_order_view', sidebar: { autoExpandOnSidebar: true } },
      },
      {
        path: 'customers', component: CustomersListComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_contact_view' },
      },
      {
        path: 'customers/customer/:id',
        component: CustomerViewComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_contact_view' },
      },
      {
        path: 'resources', component: ResourcesListComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_resource_view' },
      },
      {
        path: 'resources/resource/:id',
        component: ResourceViewComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_resource_view' },
      },
      {
        path: 'issues', component: IssuesPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'framework_issue_view', sidebar: { autoExpandOnSidebar: true } },
      },
      {
        path: 'issues/issue/:id',
        component: IssueViewComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'framework_issue_view', sidebar: { autoExpandOnSidebar: true } },
      },
      {
        // #736 §11: der volle Stream. Bewusst OHNE PermissionGuard — Benachrichtigungen hat jeder,
        // es gibt keine rollenabhaengige Sichtbarkeit (Client-Dok §6.1). Der AuthGuard der
        // Eltern-Route reicht.
        // Nicht zu verwechseln mit /profile?tab=notifications — das sind die Einstellungen und ein
        // fester Vertrag gegenueber den Mail-Footern.
        path: 'notifications',
        component: NotificationsPageComponent,
      },
      {
        path: 'articles', component: ArticlesListComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_article_view' },
      },
      { path: 'assignment',
        component: TaskAssignmentComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_scheduling_view' }
      },
      { path: 'my-tasks',
        component: MyTasksComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_scheduling_view', sidebar: { autoExpandOnSidebar: true } }
      },
      { path: 'tasks',
        component: TasksPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_scheduling_view', sidebar: { autoExpandOnSidebar: true } }
      },
      { path: 'tasks/task/:id',
        component: TaskViewComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'nexcore_scheduling_view', sidebar: { autoExpandOnSidebar: true } }
      },
      { path: 'profile',
        component: UserProfileComponent
      },
      {
        path: 'settings',
        component: SettingsComponent,
        children: [
          {
            path: 'overview',
            component: SettingsOverviewComponent
          },
          {
            path: 'users',
            component: UserListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_users_manage' },
          },
          {
            path: 'users/user/:id',
            component: UserViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_users_manage' },
          },
          {
            path: 'labels',
            component: LabelListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_labels_manage' },
          },
          {
            path: 'labels/label/:id',
            component: LabelViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_labels_manage' },
          },
          {
            path: 'groups',
            component: GroupListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_groups_manage' },
          },
          {
            path: 'groups/group/:id',
            component: GroupViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_groups_manage' },
          },
          {
            path: 'tenants',
            component: TenantListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_tenants_manage' },
          },
          {
            path: 'tenants',
            component: TenantListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_tenants_manage' },
          },
          {
            path: 'tenants/tenant/:id',
            component: TenantViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_tenants_manage' },
          },
          {
            path: 'mobileDevices',
            component: MobileDeviceListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_mobileDevice_view' },
          },
          {
            path: 'mobileDevices/mobileDevice/:id',
            component: MobileDeviceViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_mobileDevice_view' },
          },
          {
            path: 'company',
            component: CompanyViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_tenants_manage_own' },
          },
          {
            path: 'templates',
            component: TemplatesComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_templates_manage' },
          },
          {
            path: 'templates/:templateKey/:id',
            component: TemplateVariantComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_templates_manage' },
          },
          {
            path: 'developer',
            component: DeveloperPageComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_developer_view' },
          },

          {
            path: 'locations',
            component: LocationsListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'nexcore_location_view' },
          },
          {
            path: 'locations/location/:id',
            component: LocationViewComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'nexcore_location_view' },
          },
          {
            path: 'general',
            component: GeneralSettingsComponent,
            canActivate: [PermissionGuard],
          },
          {
            path: 'forms',
            component: FormsListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_forms_manage' },
          },
          {
            path: 'forms/:formKey/:id',
            component: FormVariantComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_forms_manage' },
          },
          {
            path: 'billing',
            component: BillingComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_licence_view' },
          },
          {
            path: 'timeEntryCategories',
            component: TimeEntryCategoryListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_timeEntryCategory_manage' },
          },
          {
              path: 'timeEntryCategories/timeEntryCategory/:id',
              component: TimeEntryCategoryViewComponent,
              canActivate: [PermissionGuard],
              data: { permission: 'framework_timeEntryCategory_manage' },
          },
          {
            path: 'issues',
            component: IssuesSettingsPageComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_issueSettings_manage' },
          },
          {
            path: 'tasks',
            component: TaskSettingsPageComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'nexcore_taskSettings_manage' },
          },
          // #737: Support-Flaeche fuer Zustellprobleme. Der Server prueft die Rolle in jeder
          // Operation selbst (requireSupportRole()) — der Guard ist Komfort, keine Sicherheitsgrenze.
          // Die :id der Detailroute ist die NOTIFICATION-ID, nicht die Delivery-ID.
          {
            path: 'notifications',
            component: NotificationDiagnosticsPageComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_notifications_support' },
          },
          {
            path: 'notifications/notification/:id',
            component: NotificationAdminDetailComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_notifications_support' },
          },
          // #738: Announcement-Verwaltung. Plattform-Broadcasts durch Nexato — der Server prueft die
          // Rolle in jeder Operation selbst (requirePlatformRole()), der Guard ist Komfort.
          // Achtung: der Guard prueft die LIZENZ vor der Rolle (permission.guard.ts:26-31).
          {
            path: 'announcements',
            component: AnnouncementListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee' },
          },
          // Drei Modi, eine Komponente. Der Modus steht als statisches Routen-Datum, damit die
          // Komponente ihn nicht aus dem Pfad raten muss.
          //
          // `new` MUSS vor den `:id`-Routen stehen, sonst greift `:id` fuer „new".
          {
            path: 'announcements/new',
            component: AnnouncementFormComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee', mode: 'CREATE' },
          },
          // Beide Routen leben vom Router-State der Liste: das Schema hat keine Einzelabfrage
          // (announcements(status, cursor, limit) ist die einzige Query), ein direkter Aufruf oder
          // ein Reload hat also nichts zum Nachladen und leitet zur Liste zurueck.
          {
            path: 'announcements/:id/edit',
            component: AnnouncementFormComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee', mode: 'EDIT' },
          },
          {
            path: 'announcements/:id/correct',
            component: AnnouncementFormComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee', mode: 'CORRECT' },
          },
          // #746: Job-Verwaltung. Admin-Sicht auf Quartz-Jobs und ihre Trigger, tenantuebergreifend.
          // Der Server prueft die Rolle in jedem Resolver selbst (@Secured), der Guard ist Komfort.
          {
            path: 'job-admin',
            component: JobListComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee' },
          },
          {
            path: 'job-admin/job/:group/:name',
            component: JobDetailComponent,
            canActivate: [PermissionGuard],
            data: { permission: 'framework_nexato_employee' },
          },
          { path: '', redirectTo: 'overview', pathMatch: 'full' },],
      },
      { path: 'app-no-access-page', component: NoAccessPageComponent },
      { path: 'no-license', component: NonLicensePageComponent },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ]
  },
  { path: '**', redirectTo: '' },];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
